import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Layouts
const DefaultLayout = () => import('@/components/common/layouts/DefaultLayout.vue')
const AuthLayout = () => import('@/components/common/layouts/AuthLayout.vue')
const DashboardLayout = () => import('@/components/common/layouts/DashboardLayout.vue')
const InstructorLayout = () => import('@/components/common/layouts/InstructorLayout.vue')
const AdminLayout = () => import('@/components/common/layouts/AdminLayout.vue')
const CoursePlayerLayout = () => import('@/components/common/layouts/CoursePlayerLayout.vue')

// Public pages
const Home = () => import('@/apps/student/views/Home.vue')
const CoursesCatalog = () => import('@/apps/student/views/CoursesCatalog.vue')
const CourseDetail = () => import('@/apps/student/views/CourseDetail.vue')
const InstructorProfile = () => import('@/views/public/InstructorProfile.vue')
const CertificateVerify = () => import('@/views/public/CertificateVerify.vue')

// Auth pages
const Login = () => import('@/apps/student/views/auth/Login.vue')
const Register = () => import('@/apps/student/views/auth/Register.vue')
const ForgotPassword = () => import('@/apps/student/views/auth/ForgotPassword.vue')

// Student pages
const StudentDashboard = () => import('@/apps/student/views/Dashboard.vue')
const MyCourses = () => import('@/apps/student/views/MyCourses.vue')
const CoursePlayer = () => import('@/apps/student/views/CoursePlayer.vue')
const StudentProfile = () => import('@/apps/student/views/Profile.vue')
const Certificates = () => import('@/apps/student/views/Certificates.vue')
const Messages = () => import('@/apps/student/views/Messages.vue')
const Achievements = () => import('@/apps/student/views/Achievements.vue')
// New student pages
const StudentQuizzes = () => import('@/apps/student/views/Quizzes.vue')
const StudentAssignments = () => import('@/apps/student/views/Assignments.vue')
const StudentGrades = () => import('@/apps/student/views/Grades.vue')
const StudentPlanner = () => import('@/apps/student/views/StudyPlanner.vue')
const StudentResources = () => import('@/apps/student/views/Resources.vue')

// Instructor pages
const InstructorDashboard = () => import('@/apps/instructor/views/Dashboard.vue')
const InstructorCourses = () => import('@/apps/instructor/views/Courses.vue')
const CourseCreate = () => import('@/apps/instructor/views/CourseCreate.vue')
const CourseBuilder = () => import('@/apps/instructor/views/CourseBuilder.vue')
const CourseStudents = () => import('@/apps/instructor/views/CourseStudents.vue')
const InstructorStudents = () => import('@/apps/instructor/views/Students.vue')
const InstructorGradebook = () => import('@/apps/instructor/views/Gradebook.vue')
const InstructorExams = () => import('@/apps/instructor/views/Exams.vue')
const QuestionBank = () => import('@/apps/instructor/views/QuestionBank.vue')
const InstructorAnnouncements = () => import('@/apps/instructor/views/Announcements.vue')
const InstructorQA = () => import('@/apps/instructor/views/QandA.vue')
const InstructorReviews = () => import('@/apps/instructor/views/Reviews.vue')
const InstructorCertificates = () => import('@/apps/instructor/views/Certificates.vue')
const InstructorAnalytics = () => import('@/apps/instructor/views/Analytics.vue')
// New academic instructor pages
const InstructorAttendance = () => import('@/apps/instructor/views/Attendance.vue')
const InstructorAssignments = () => import('@/apps/instructor/views/Assignments.vue')
const InstructorCalendar = () => import('@/apps/instructor/views/Calendar.vue')
const InstructorRubrics = () => import('@/apps/instructor/views/Rubrics.vue')
const InstructorReports = () => import('@/apps/instructor/views/Reports.vue')
const InstructorGroups = () => import('@/apps/instructor/views/Groups.vue')
const InstructorMessages = () => import('@/apps/instructor/views/Messages.vue')
const InstructorResources = () => import('@/apps/instructor/views/Resources.vue')
// New teacher tools
const InstructorLessonPlanner = () => import('@/apps/instructor/views/LessonPlanner.vue')
const InstructorStudentTracking = () => import('@/apps/instructor/views/StudentTracking.vue')
const InstructorStudentObserver = () => import('@/apps/instructor/views/StudentObserver.vue')
const InstructorTutoring = () => import('@/apps/instructor/views/Tutoring.vue')
const InstructorClassDiary = () => import('@/apps/instructor/views/ClassDiary.vue')
const InstructorAreaPlan = () => import('@/apps/instructor/views/AreaPlan.vue')

// Admin pages
const AdminDashboard = () => import('@/apps/admin/views/Dashboard.vue')
const AdminUsers = () => import('@/apps/admin/views/Users.vue')
const AdminCourses = () => import('@/apps/admin/views/Courses.vue')
const AdminPayments = () => import('@/apps/admin/views/Payments.vue')
const AdminReports = () => import('@/apps/admin/views/Reports.vue')
const AdminCalendar = () => import('@/apps/admin/views/Calendar.vue')
const AdminCommunications = () => import('@/apps/admin/views/Communications.vue')
const AdminEnrollments = () => import('@/apps/admin/views/Enrollments.vue')
const AdminAttendance = () => import('@/apps/admin/views/Attendance.vue')
const AdminSettings = () => import('@/apps/admin/views/Settings.vue')

const routes = [
  // Public routes
  {
    path: '/',
    component: DefaultLayout,
    children: [
      { path: '', name: 'home', component: Home },
      { path: 'courses', name: 'courses-catalog', component: CoursesCatalog },
      { path: 'courses/:slug', name: 'course-detail', component: CourseDetail },
      { path: 'instructor/:id', name: 'instructor-profile', component: InstructorProfile },
      { path: 'verify/:code', name: 'certificate-verify', component: CertificateVerify },
    ],
  },

  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    meta: { guest: true },
    children: [
      { path: 'login', name: 'login', component: Login },
      // Registration disabled for demo mode - redirect to login
      {
        path: 'register',
        name: 'register',
        redirect: { name: 'login' }
      },
      { path: 'forgot-password', name: 'forgot-password', component: ForgotPassword },
    ],
  },

  // Student routes
  {
    path: '/learn',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'student-dashboard', component: StudentDashboard },
      { path: 'my-courses', name: 'my-courses', component: MyCourses },
      { path: 'profile', name: 'student-profile', component: StudentProfile },
      { path: 'certificates', name: 'certificates', component: Certificates },
      { path: 'messages', name: 'messages', component: Messages },
      { path: 'achievements', name: 'achievements', component: Achievements },
      // New student tools
      { path: 'quizzes', name: 'student-quizzes', component: StudentQuizzes },
      { path: 'assignments', name: 'student-assignments', component: StudentAssignments },
      { path: 'grades', name: 'student-grades', component: StudentGrades },
      { path: 'planner', name: 'student-planner', component: StudentPlanner },
      { path: 'resources', name: 'student-resources', component: StudentResources },
    ],
  },

  // Course Player - separate layout (no sidebar)
  {
    path: '/learn/course/:slug',
    component: CoursePlayerLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'course-player', component: CoursePlayer },
    ],
  },

  // Instructor routes
  {
    path: '/instructor',
    component: InstructorLayout,
    meta: { requiresAuth: true, requiresInstructor: true },
    children: [
      { path: '', name: 'instructor-dashboard', component: InstructorDashboard },
      // Course management
      { path: 'courses', name: 'instructor-courses', component: InstructorCourses },
      { path: 'courses/new', name: 'course-create', component: CourseCreate },
      { path: 'courses/:slug/edit', name: 'course-builder', component: CourseBuilder },
      { path: 'courses/:slug/students', name: 'course-students', component: CourseStudents },
      // Groups and sections
      { path: 'groups', name: 'instructor-groups', component: InstructorGroups },
      // Exams and evaluation
      { path: 'exams', name: 'instructor-exams', component: InstructorExams },
      { path: 'question-bank', name: 'question-bank', component: QuestionBank },
      { path: 'assignments', name: 'instructor-assignments', component: InstructorAssignments },
      { path: 'rubrics', name: 'instructor-rubrics', component: InstructorRubrics },
      // Students and grades
      { path: 'students', name: 'instructor-students', component: InstructorStudents },
      { path: 'gradebook', name: 'instructor-gradebook', component: InstructorGradebook },
      { path: 'attendance', name: 'instructor-attendance', component: InstructorAttendance },
      { path: 'certificates', name: 'instructor-certificates', component: InstructorCertificates },
      // Communication
      { path: 'announcements', name: 'instructor-announcements', component: InstructorAnnouncements },
      { path: 'messages', name: 'instructor-messages', component: InstructorMessages },
      { path: 'qa', name: 'instructor-qa', component: InstructorQA },
      { path: 'reviews', name: 'instructor-reviews', component: InstructorReviews },
      // Resources and calendar
      { path: 'resources', name: 'instructor-resources', component: InstructorResources },
      { path: 'calendar', name: 'instructor-calendar', component: InstructorCalendar },
      // Analytics and reports
      { path: 'analytics', name: 'instructor-analytics', component: InstructorAnalytics },
      { path: 'reports', name: 'instructor-reports', component: InstructorReports },
      // Teacher tools
      { path: 'lesson-planner', name: 'instructor-lesson-planner', component: InstructorLessonPlanner },
      { path: 'student-tracking', name: 'instructor-student-tracking', component: InstructorStudentTracking },
      { path: 'student-observer', name: 'instructor-student-observer', component: InstructorStudentObserver },
      { path: 'tutoring', name: 'instructor-tutoring', component: InstructorTutoring },
      { path: 'class-diary', name: 'instructor-class-diary', component: InstructorClassDiary },
      { path: 'area-plan', name: 'instructor-area-plan', component: InstructorAreaPlan },
    ],
  },

  // Admin routes
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '', name: 'admin-dashboard', component: AdminDashboard },
      { path: 'users', name: 'admin-users', component: AdminUsers },
      { path: 'courses', name: 'admin-courses', component: AdminCourses },
      { path: 'payments', name: 'admin-payments', component: AdminPayments },
      { path: 'reports', name: 'admin-reports', component: AdminReports },
      { path: 'calendar', name: 'admin-calendar', component: AdminCalendar },
      { path: 'communications', name: 'admin-communications', component: AdminCommunications },
      { path: 'enrollments', name: 'admin-enrollments', component: AdminEnrollments },
      { path: 'attendance', name: 'admin-attendance', component: AdminAttendance },
      { path: 'settings', name: 'admin-settings', component: AdminSettings },
    ],
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/components/common/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

// Helper function to get default dashboard by role
function getDefaultDashboard(role) {
  switch (role) {
    case 'admin':
      return 'admin-dashboard'
    case 'instructor':
      return 'instructor-dashboard'
    default:
      return 'student-dashboard'
  }
}

// Check if user role is allowed for a route
function isRoleAllowedForRoute(route, userRole) {
  // Check path-based restrictions
  const path = route.path

  // Admin paths - only admin
  if (path.startsWith('/admin')) {
    return userRole === 'admin'
  }

  // Instructor paths - admin and instructor
  if (path.startsWith('/instructor')) {
    return userRole === 'admin' || userRole === 'instructor'
  }

  // Student paths - all authenticated users
  if (path.startsWith('/learn')) {
    return true
  }

  // Public paths
  return true
}

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Wait for auth check to complete
  if (!authStore.initialized) {
    await authStore.checkAuth()
  }

  const isAuthenticated = authStore.isAuthenticated
  const user = authStore.user
  const userRole = user?.role || 'student'

  // Guest only routes (login, register) - redirect to appropriate dashboard
  if (to.meta.guest && isAuthenticated) {
    return next({ name: getDefaultDashboard(userRole) })
  }

  // Protected routes
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // Skip further checks for unauthenticated users on public routes
  if (!isAuthenticated) {
    return next()
  }

  // Role-based path protection (stricter check using path)
  if (!isRoleAllowedForRoute(to, userRole)) {
    // Redirect to appropriate dashboard
    return next({ name: getDefaultDashboard(userRole) })
  }

  // Instructor routes - instructors and admins can access
  if (to.meta.requiresInstructor && userRole !== 'instructor' && userRole !== 'admin') {
    return next({ name: 'student-dashboard' })
  }

  // Admin routes - only admins can access
  if (to.meta.requiresAdmin && userRole !== 'admin') {
    return next({ name: getDefaultDashboard(userRole) })
  }

  // Check demo session timeout
  const demoMode = localStorage.getItem('demo_mode') === 'true'
  const loginTime = parseInt(localStorage.getItem('demo_login_time') || '0')
  const sessionTimeout = 10 * 60 * 1000 // 10 minutes

  if (demoMode && loginTime && (Date.now() - loginTime > sessionTimeout)) {
    // Session expired
    localStorage.removeItem('demo_mode')
    localStorage.removeItem('demo_login_time')
    authStore.logout()
    return next({ name: 'login', query: { reason: 'timeout' } })
  }

  next()
})

export default router
